// useRatingDraft — server-side persistence for a fitter's in-progress rating pass (M3).
//
// Replaces the six localStorage keys per (profile, category) that DetailedRatingModal_v2
// used to juggle. Those keys only existed on the machine the fitter started on, so
// switching laptops or clearing the browser lost the whole pass; for a ~1000-combination
// task that is enough to lose a fitter.
//
// Deliberately shape-agnostic: the payload is an opaque object owned by the caller, which
// serialises its own Maps on the way in and rehydrates them on the way out. The §2A
// rewrite replaces the rating surface (tier groups → rank the top tier) and changes what
// a draft *contains* — this hook and the endpoints behind it survive that unchanged.
//
// localStorage stays on as a write-behind cache so a save attempted offline is not lost;
// on load the newer of (cache, server) wins by updated_at, and a newer cache is pushed up.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  saveRatingDraft,
  getRatingDrafts,
  deleteRatingDraft,
  RatingCategory
} from '../services/expertDetailedRatingService';

export type DraftPayload = Record<string, any>;

export type DraftStatus = 'idle' | 'loading' | 'saving' | 'saved' | 'offline';

const SAVE_DEBOUNCE_MS = 5000;

const CATEGORIES: RatingCategory[] = ['driver', 'iron', 'wedge'];

const cacheKey = (profileId: number, category: RatingCategory) =>
  `expert_draft_${profileId}_${category}`;

interface CachedDraft {
  payload: DraftPayload;
  updated_at: string;
}

const readCache = (profileId: number, category: RatingCategory): CachedDraft | null => {
  try {
    const raw = localStorage.getItem(cacheKey(profileId, category));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (profileId: number, category: RatingCategory, draft: CachedDraft) => {
  try {
    localStorage.setItem(cacheKey(profileId, category), JSON.stringify(draft));
  } catch {
    // Quota or private-mode failure. The server copy is the real one; losing the
    // offline cache is survivable, so don't break the rating flow over it.
  }
};

const clearCache = (profileId: number, category: RatingCategory) => {
  localStorage.removeItem(cacheKey(profileId, category));
};

export interface UseRatingDraftResult {
  /** Restored payload, or null if there is no saved work. Undefined until loaded. */
  draft: DraftPayload | null;
  /** False until the initial server load has settled — don't overwrite before this. */
  loaded: boolean;
  status: DraftStatus;
  /** Queue a debounced save. Safe to call on every keystroke. */
  saveDraft: (payload: DraftPayload) => void;
  /** Write immediately (submit paths, tab close). Resolves when the server has it. */
  flushDraft: () => Promise<void>;
  /** Discard this category's saved work, server and cache. */
  clearDraft: () => Promise<void>;
  /** Discard all three categories for this profile. */
  clearProfileDrafts: () => Promise<void>;
}

export function useRatingDraft(
  profileId: number,
  category: RatingCategory,
  enabled: boolean = true
): UseRatingDraftResult {
  const [draft, setDraft] = useState<DraftPayload | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState<DraftStatus>('idle');

  // Latest payload waiting to go up, and the timer that will send it. Refs rather than
  // state because the debounce must not re-render or restart on every edit.
  const pendingRef = useRef<DraftPayload | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const push = useCallback(async (payload: DraftPayload) => {
    setStatus('saving');
    try {
      const res = await saveRatingDraft(profileId, category, payload);
      writeCache(profileId, category, { payload, updated_at: res.updated_at });
      pendingRef.current = null;
      setStatus('saved');
    } catch {
      // Offline or server down: keep it locally with a local timestamp so the next
      // successful load can detect that the cache is ahead and push it up.
      writeCache(profileId, category, { payload, updated_at: new Date().toISOString() });
      setStatus('offline');
    }
  }, [profileId, category]);

  // Initial load: server is the source of truth unless the cache is provably newer.
  useEffect(() => {
    if (!enabled || !profileId) return;

    let cancelled = false;
    setLoaded(false);
    setStatus('loading');

    (async () => {
      const cached = readCache(profileId, category);
      try {
        const res = await getRatingDrafts(profileId);
        if (cancelled) return;

        const server = res.drafts?.[category] ?? null;
        const cacheIsNewer =
          cached && (!server || cached.updated_at > server.updated_at);

        if (cacheIsNewer) {
          // Edits made while the server was unreachable. Reinstate them upstream.
          setDraft(cached!.payload);
          await push(cached!.payload);
        } else if (server) {
          setDraft(server.payload);
          writeCache(profileId, category, server);
          setStatus('saved');
        } else {
          setDraft(null);
          clearCache(profileId, category);
          setStatus('idle');
        }
      } catch {
        if (cancelled) return;
        // Server unreachable at open. Work from the cache rather than showing an
        // empty grid, which would read as "my work is gone".
        setDraft(cached ? cached.payload : null);
        setStatus('offline');
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [profileId, category, enabled, push]);

  const saveDraft = useCallback((payload: DraftPayload) => {
    pendingRef.current = payload;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (pendingRef.current) push(pendingRef.current);
    }, SAVE_DEBOUNCE_MS);
  }, [push]);

  const flushDraft = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (pendingRef.current) await push(pendingRef.current);
  }, [push]);

  const clearDraft = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = null;
    clearCache(profileId, category);
    setDraft(null);
    setStatus('idle');
    try {
      await deleteRatingDraft(profileId, category);
    } catch {
      setStatus('offline');
    }
  }, [profileId, category]);

  const clearProfileDrafts = useCallback(async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = null;
    CATEGORIES.forEach(cat => clearCache(profileId, cat));
    setDraft(null);
    setStatus('idle');
    try {
      await deleteRatingDraft(profileId);
    } catch {
      setStatus('offline');
    }
  }, [profileId]);

  // A debounced save in flight when the tab closes would otherwise be lost. The cache
  // write is synchronous, so it lands even though an XHR at this point would not.
  useEffect(() => {
    const handler = () => {
      if (pendingRef.current) {
        writeCache(profileId, category, {
          payload: pendingRef.current,
          updated_at: new Date().toISOString()
        });
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('beforeunload', handler);
      handler();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [profileId, category]);

  return { draft, loaded, status, saveDraft, flushDraft, clearDraft, clearProfileDrafts };
}
